// `/outcome` — THE APP'S "LOG THE OUTCOME" SHEET (WEB-PARITY-7, OC-1 to OC-8; LogOutcomeView.swift).
//
// THE SIX OUTCOMES ARE READ FROM THE CONTRACT. `OutcomeResult` is the enum, so a seventh outcome added server-side is a
// compile error here rather than a value the screen silently drops (§3 row 27). Their labels are the app's (WEB-COPY-1
// item 4, `heldCopy.ts`), and each row now carries the app's icon in the result's tint (OC-4).
//
// THE SCREEN IS THE APP'S, TOP TO BOTTOM (LogOutcomeView.body, :60-88): the title and its intro (OC-1); the trophy hero
// once a win has a final value and a saving (OC-4); "WHAT HAPPENED?" and the six rows; the final-value card ONLY for
// the results the app captures one for (OC-2, `capturesFinalValue`); the next-steps card for a filed, denied, partial or
// raised result (OC-5); "Anything to remember?" with the note (OC-6) and the "Help other homeowners" switch with the
// approved sharing sentence (OC-7, WEB-COPY-1 item 5); then "Save outcome", and — when the server answers that a DECIDED
// result made the owner eligible — the app's thanks line and "Done" in its place (OC-8, J1).
//
// THE LIVE LINES ARE THE SERVER'S ARITHMETIC (`@v2-outcome-rules`). "≈ $X lower than your $Y" and "about $Z/yr saved"
// change as the owner types, before anything is saved, so no call can answer them in time. The reduction and the saving
// are the server's own functions — `derivedReduction`, `raisedBy`, `annualSavingOf` — over the server's own assessed value
// (the snapshot) and the server's own rate and ratio (`AppealSummary.outcomeRate`). What is recorded is the server's
// work again: it derives the reduction from its stored assessment, never from this screen.
//
// SHARING IS ON BY DEFAULT, AS IN THE APP (WEB-COPY-1, item 5; `SHARING_DEFAULT`, pinned to LogOutcomeView.swift:22),
// and an outcome logged before is PREFILLED as the app's `prefill` does (:288-294) — result, final value, note and the
// sharing answer, from the server's snapshot. An answer the server did not keep (an outcome recorded before 2.19.0) is
// the app's default, never a guess.
//
// J3 (ACCOUNTS_PLAN §3 J, approved 2026-09-30) shows only with the server's WEB_ACCOUNTS_ENABLED on, before saving, when
// the appeal's letter has shipped and the server says no returning-owner offer is waiting.

import { useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AppealSnapshot, AppealSummary, OutcomeResult } from "@contract";
import { annualSavingOf, capturesFinalValue, derivedReduction, isDecided, raisedBy } from "@v2-outcome-rules";
import { useWebAccounts } from "../app/accounts";
import { useAppealId, useSession } from "../app/session";
import { useAction, useCall } from "../app/useCall";
import { HeldLine } from "../components/copy";
import { afterTemplate, afterText } from "../components/afterFilingCopy";
import { SHARING_DEFAULT, heldText, type HeldCopyId } from "../components/heldCopy";
import { Actions } from "../components/layout";
import { Refusal } from "../components/Refusal";
import { dollars, money } from "../format";
import { AppSay, AppSayTemplate, ResultIcon, TrophyMark, tintOf } from "./afterFilingParts";

/** Every member of the contract's enum, in the app's order (`OutcomeResult.allCases`). Adding one breaks this line. */
const OUTCOMES: readonly OutcomeResult[] = ["filed", "won", "partiallyWon", "denied", "raised", "withdrawn"];

/** Each result's held title and description, keyed by the contract's own value (item 4). */
const LABELS: Readonly<Record<OutcomeResult, { title: HeldCopyId; subtitle: HeldCopyId }>> = {
  filed: { title: "outcomes.labels.filed.title", subtitle: "outcomes.labels.filed.subtitle" },
  won: { title: "outcomes.labels.won.title", subtitle: "outcomes.labels.won.subtitle" },
  partiallyWon: { title: "outcomes.labels.partiallyWon.title", subtitle: "outcomes.labels.partiallyWon.subtitle" },
  denied: { title: "outcomes.labels.denied.title", subtitle: "outcomes.labels.denied.subtitle" },
  raised: { title: "outcomes.labels.raised.title", subtitle: "outcomes.labels.raised.subtitle" },
  withdrawn: { title: "outcomes.labels.withdrawn.title", subtitle: "outcomes.labels.withdrawn.subtitle" },
};

/** What the owner typed into "Final assessed value", as whole dollars, or null for nothing usable. */
function typedDollars(text: string): number | null {
  const digits = text.replace(/[^0-9]/g, "");
  if (digits === "") return null;
  const n = Number(digits);
  return Number.isSafeInteger(n) ? n : null;
}

/** "the ~1.49% PA average tax rate" — `effectiveRateLabel` (LogOutcomeView.swift:52-58): the rate as a percent with zero to
 *  two decimals, the way `.number.precision(.fractionLength(0...2))` writes it. */
function rateLabel(rate: NonNullable<AppealSummary["outcomeRate"]>): string {
  const pct = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(rate.effectiveRate * 100);
  return afterTemplate("outcome.final.rateLabel", { pct, state: rate.state });
}

export function Outcome() {
  const session = useSession();
  const appealId = useAppealId();
  // The appeal and its figures come from the server's list (one call); the offer from the account's (J3).
  const list = useCall(() => session.transport.v2Outcomes.list(), [session.transport, session.revision]);
  const account = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);

  if (list.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading your appeals…
      </p>
    );
  }
  if (list.status === "failed") return <Refusal failure={list.failure} context="Your outcome" />;
  const snapshot = (list.response.body.appeals ?? []).find((a) => a.appealId === appealId) ?? list.response.appeal;
  const summary = (list.response.body.summaries ?? []).find((s) => s.appealId === appealId) ?? null;
  const offerWaiting = account.status === "ok" ? account.response.body.offers.returningOwner.eligible : null;
  // Keyed on the appeal: a different appeal is a fresh sheet, as the app presents a new one.
  return <OutcomeSheet key={appealId} appealId={appealId} snapshot={snapshot ?? null} summary={summary} offerWaiting={offerWaiting} />;
}

function OutcomeSheet({ appealId, snapshot, summary, offerWaiting }: {
  appealId: string;
  snapshot: AppealSnapshot | null;
  summary: AppealSummary | null;
  offerWaiting: boolean | null;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const webAccounts = useWebAccounts();
  const logged = snapshot?.outcome ?? null;
  // THE APP'S PREFILL (LogOutcomeView.prefill, :288-294), from what the server kept.
  const [picked, setPicked] = useState<OutcomeResult | null>(logged?.result ?? null);
  const [finalText, setFinalText] = useState(logged?.finalAssessedValue == null ? "" : String(logged.finalAssessedValue.amount));
  const [note, setNote] = useState(logged?.note ?? "");
  const [shared, setShared] = useState<boolean>(logged?.sharedAnonymously ?? SHARING_DEFAULT);
  const [thanks, setThanks] = useState(false);
  const noteId = useId();
  const finalId = useId();

  const captures = picked !== null && capturesFinalValue(picked);
  const final = captures ? typedDollars(finalText) : null;
  const assessed = snapshot?.subject?.assessedValue?.value.amount ?? null;
  const reduction = derivedReduction(assessed, final);
  const rate = summary?.outcomeRate ?? null;
  const annual = annualSavingOf(reduction, rate?.effectiveRate ?? null, rate?.ratio ?? 0);
  const increase = picked === "raised" ? raisedBy(assessed, final) : null;

  const record = useAction(() =>
    session.transport.v2Outcomes.record({
      appealId,
      result: picked as OutcomeResult,
      sharedAnonymously: shared,
      ...(final === null ? {} : { finalAssessedValue: { basis: "assessed" as const, amount: final } }),
      ...(note.trim() === "" ? {} : { note }),
    }),
  );

  const save = async () => {
    const result = await record.run();
    if (!result.ok) return;
    session.refresh();
    // LogOutcomeView.save (:322-326): the thanks only for a DECIDED result the server answered eligible; else close.
    const eligible = result.response.body.recorded?.returningOwner.eligible === true;
    if (picked !== null && isDecided(picked) && eligible) setThanks(true);
    else navigate("/home");
  };

  const heroShown = (picked === "won" || picked === "partiallyWon") && annual !== null && reduction !== null;
  const letterShipped = snapshot?.letter.status === "shipped";
  const j3 = webAccounts === true && !thanks && letterShipped && offerWaiting === false;

  return (
    <div className="log-outcome" data-outcome-screen>
      <AppSay id="outcome.navTitle" as="h1" />

      {heroShown && (
        <section className="trophy-hero" data-trophy-hero>
          <TrophyMark />
          <AppSay id={picked === "won" ? "outcome.hero.won" : "outcome.hero.partiallyWon"} className="eyebrow savings" />
          <AppSayTemplate id="outcome.hero.saved" values={{ amount: dollars(annual) }} className="hero-figure" />
          {final !== null && (
            <AppSayTemplate
              id="outcome.hero.reducedTo"
              values={{ final: dollars(final), reduction: dollars(reduction) }}
              className="faint"
            />
          )}
        </section>
      )}

      <AppSay id="outcome.intro" className="muted" />

      <AppSay id="outcome.picker.title" as="h2" className="section-label" />
      <div className="outcomes" role="radiogroup" aria-label={afterText("outcome.picker.title")}>
        {OUTCOMES.map((outcome) => (
          <button
            key={outcome}
            type="button"
            role="radio"
            aria-checked={picked === outcome}
            data-outcome={outcome}
            className={`outcome tint-${tintOf(outcome)}${picked === outcome ? " on" : ""}`}
            onClick={() => {
              setPicked(outcome);
              // The app clears the figure when a result that captures none is picked (LogOutcomeView.resultRow, :146).
              if (!capturesFinalValue(outcome)) setFinalText("");
            }}
          >
            <ResultIcon result={outcome} />
            <span>
              <b data-held-copy={LABELS[outcome].title}>{heldText(LABELS[outcome].title)}</b>
              <em data-held-copy={LABELS[outcome].subtitle}>{heldText(LABELS[outcome].subtitle)}</em>
            </span>
            <span className="radio" aria-hidden="true" />
          </button>
        ))}
      </div>

      {captures && (
        <section className="card brand-card" data-final-value-card>
          <AppSay id="outcome.final.title" as="h3" />
          <div className="field">
            <label htmlFor={finalId} data-app-copy="outcome.final.field">{afterText("outcome.final.field")}</label>
            <div className="dollar-input">
              <span aria-hidden="true">$</span>
              <input
                id={finalId}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={finalText}
                onChange={(e) => setFinalText(e.currentTarget.value)}
              />
            </div>
          </div>
          {reduction !== null ? (
            <div className="final-lines" data-reduction={reduction}>
              <AppSayTemplate
                id="outcome.final.lower"
                values={{ reduction: dollars(reduction), assessed: assessed === null ? "" : money({ basis: "assessed", amount: assessed }) }}
                className="savings strong"
              />
              {annual !== null && rate !== null && (
                <AppSayTemplate
                  id="outcome.final.saved"
                  values={{ amount: dollars(annual), rate: rateLabel(rate) }}
                  className="faint small"
                />
              )}
            </div>
          ) : increase !== null && assessed !== null ? (
            <AppSayTemplate
              id="outcome.final.higher"
              values={{ increase: dollars(increase), assessed: money({ basis: "assessed", amount: assessed }) }}
              className="danger small strong"
            />
          ) : final !== null ? (
            <AppSay id="outcome.final.notBelow" className="faint small" />
          ) : null}
        </section>
      )}

      {picked === "filed" && (
        <section className="card brand-card" data-next-steps="filed">
          <AppSay id="outcome.next.filed.title" as="h3" />
          <AppSay id="outcome.next.filed.body" className="muted small" />
        </section>
      )}
      {(picked === "denied" || picked === "partiallyWon" || picked === "raised") && (
        <section className="card brand-card" data-next-steps="further">
          <AppSay id="outcome.next.further.title" as="h3" />
          <AppSay id="outcome.next.further.body" className="muted small" />
        </section>
      )}

      <section className="card brand-card" data-details-card>
        <label htmlFor={noteId}>
          <AppSay id="outcome.note.title" as="h3" />
        </label>
        <textarea
          id={noteId}
          rows={3}
          maxLength={4000}
          placeholder={afterText("outcome.note.placeholder")}
          data-app-placeholder="outcome.note.placeholder"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <div className="switch-row">
          <span>
            <AppSay id="outcome.share.title" as="b" />
            <HeldLine id="outcomes.sharingConsent" className="faint small" />
          </span>
          <button
            type="button"
            role="switch"
            className={`switch${shared ? " on" : ""}`}
            aria-checked={shared}
            aria-label={afterText("outcome.share.title")}
            data-sharing={shared ? "on" : "off"}
            onClick={() => setShared((s) => !s)}
          >
            <span className="knob" aria-hidden="true" />
          </button>
        </div>
      </section>

      {j3 && <HeldLine id="account.returning.beforeDecision" className="note good" />}

      {record.state.status === "failed" && <Refusal failure={record.state.failure} context="Your outcome" />}

      {thanks ? (
        <div className="save-bar thanks" data-returning-thanks>
          <AppSay id="outcome.returningThanks" className="savings strong" />
          <Actions>
            <Link className="btn primary" to="/home" data-app-copy="outcome.done">
              {afterText("outcome.done")}
            </Link>
          </Actions>
        </div>
      ) : (
        <Actions>
          <Link className="btn ghost" to="/home" data-app-copy="outcome.cancel">
            {afterText("outcome.cancel")}
          </Link>
          <button
            className="btn primary"
            type="button"
            disabled={picked === null || record.state.status === "pending"}
            aria-busy={record.state.status === "pending" || undefined}
            onClick={() => void save()}
            data-app-copy="outcome.save"
          >
            {afterText("outcome.save")}
          </button>
        </Actions>
      )}
    </div>
  );
}
